import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TableSortLabel,
  Stack,
  InputAdornment,
  Autocomplete,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Search, ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import BasicButton from '../../../components/button/BasicButton';
import useAdminDraftGuard from '../useAdminDraftGuard';
import { useUserStore } from '../../../stores/userStore';
import { adminApi } from '../../../api/admin';
import ProgressBar from '../../../components/common/ProgressBar';
import NoData from '../../../components/common/NoData';
import Pagination from '../../../components/common/Pagination';
import theme from '../../../theme';

const ROWS_PER_PAGE = 20;

const HEAD_CELLS = [
  { id: 'ARTIST_NAME', label: '아티스트', sortable: true },
  { id: 'ALBUM_TITLE', label: '앨범', sortable: true },
  { id: 'RELEASE_DATE', label: '발매일', sortable: true },
  { id: 'VIEW_COUNT', label: '조회수', sortable: true },
  { id: 'REVIEW_COUNT', label: '리뷰수', sortable: true },
  { id: 'BOOKMARK_COUNT', label: '저장수', sortable: true },
];

const ReviewAlbumSession = ({ searchParams, updateURL, onCountsChange, starred }) => {
  const debouncedSearchTerm = searchParams.get('search') || '';
  const sortField = searchParams.get('sortField') || 'ALBUM_TITLE';
  const sortOrder = searchParams.get('sortOrder') || 'asc';
  const parsedPage = Number(searchParams.get('page'));
  const currentPage = Number.isSafeInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1;

  const [searchTerm, setSearchTerm] = useState(debouncedSearchTerm);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState({ totalPages: 1, totalItems: 0 });
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [selectedAlbumIds, setSelectedAlbumIds] = useState([]);
  const [confirmation, setConfirmation] = useState(null);
  const [mutationError, setMutationError] = useState('');
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const alive = useRef(true);
  const action = starred ? '해제' : '추가';
  useAdminDraftGuard(null, { busy });
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    setSearchTerm(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    let current = true;
    const fetchAlbums = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await (starred ? adminApi.getStarredAlbums : adminApi.getActiveAlbums)(
          currentPage,
          ROWS_PER_PAGE,
          debouncedSearchTerm,
          sortField,
          sortOrder,
        );
        if (!current) return;
        setAlbums(response.data || []);
        setPaginationInfo(response.meta.pagination);
      } catch (err) {
        if (!current) return;
        setError(err);
        setAlbums([]);
        setPaginationInfo({ totalPages: 1, totalItems: 0 });
      } finally {
        if (current) setLoading(false);
      }
    };

    fetchAlbums();
    return () => {
      current = false;
    };
  }, [currentPage, debouncedSearchTerm, refetchTrigger, sortField, sortOrder, starred]);

  const handleSearchClick = () => {
    updateURL({ search: searchTerm || null, page: null });
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter' && !event.nativeEvent?.isComposing && event.keyCode !== 229) {
      handleSearchClick();
    }
  };

  const handleSort = (field) => {
    const isCurrentField = sortField === field;
    const newSortOrder = isCurrentField && sortOrder === 'desc' ? 'asc' : 'desc';
    updateURL({ sortField: field, sortOrder: newSortOrder, page: null });
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const currentPageAlbumIds = albums.map((album) => album.albumId);
      setSelectedAlbumIds((prev) => [...new Set([...prev, ...currentPageAlbumIds])]);
      return;
    }
    const currentPageAlbumIds = albums.map((album) => album.albumId);
    setSelectedAlbumIds((prev) => prev.filter((id) => !currentPageAlbumIds.includes(id)));
  };

  const handleSelectClick = (event, id) => {
    const selectedIndex = selectedAlbumIds.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedAlbumIds, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedAlbumIds.slice(1));
    } else if (selectedIndex === selectedAlbumIds.length - 1) {
      newSelected = newSelected.concat(selectedAlbumIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedAlbumIds.slice(0, selectedIndex),
        selectedAlbumIds.slice(selectedIndex + 1),
      );
    }
    setSelectedAlbumIds(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    updateURL({ page: newPage });
  };

  const handleConfirm = async () => {
    if (pending.current || !confirmation?.length) return;
    pending.current = true;
    setBusy(true);
    setMutationError('');
    try {
      await (starred ? adminApi.unstarAlbums : adminApi.starAlbums)(confirmation);
      if (!alive.current) return;
      setConfirmation(null);
      setSelectedAlbumIds([]);
      setRefetchTrigger((c) => c + 1);
      onCountsChange?.();
    } catch (err) {
      if (alive.current) setMutationError(`P 마크 ${action}에 실패했습니다. 다시 시도해주세요.`);
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  };

  const numSelectedOnPage = useMemo(
    () => albums.filter((album) => selectedAlbumIds.includes(album.albumId)).length,
    [albums, selectedAlbumIds],
  );

  const isAllOnPageSelected = albums.length > 0 && numSelectedOnPage === albums.length;
  const isIndeterminate = numSelectedOnPage > 0 && !isAllOnPageSelected;
  const hasSelection = selectedAlbumIds.length > 0;

  const cellStyle = { borderRight: `1px solid ${theme.palette.grey.line}` };
  const textStyle = { fontSize: 14, color: theme.palette.black[9] };
  const buttonStyle = { typography: 'body3', fontWeight: 'medium', padding: '12px 16px' };

  return (
    <>
      {/* 검색 */}
      <Stack spacing={'8px'} sx={{ mb: '30px' }}>
        <Typography fontSize={14} color={theme.palette.black[7]}>
          앨범 검색
        </Typography>
        <Box
          sx={{
            p: 1.5,
            width: '100%',
            border: `1px solid ${theme.palette.grey.line}`,
            borderRadius: 0.5,
          }}
        >
          <Autocomplete
            options={[]}
            freeSolo
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                placeholder="아티스트 또는 앨범 검색"
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search
                        sx={{
                          width: { xs: 20, lg: 16 },
                          height: { xs: 20, lg: 16 },
                          color: theme.palette.black[1],
                        }}
                      />
                    </InputAdornment>
                  ),
                  onKeyDown: handleSearchKeyDown,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '16px', lg: '14px' },
                    padding: '0 !important',
                  },
                  '& .MuiInputBase-root': {
                    paddingRight: '0 !important',
                  },
                }}
              />
            )}
            value={searchTerm}
            inputValue={searchTerm}
            onSubmit={handleSearchClick}
            onInputChange={(_, newInputValue) => {
              setSearchTerm(newInputValue);
            }}
          />
        </Box>
      </Stack>

      {/* 액션 버튼 */}
      <BasicButton
        variant="outlined"
        onClick={() => {
          setMutationError('');
          setConfirmation([...selectedAlbumIds]);
        }}
        disabled={!hasSelection || loading || Boolean(error) || busy}
        sx={{ ...buttonStyle, mb: { xs: '20px', md: '16px' } }}
      >
        P 마크 {action}
      </BasicButton>

      <Typography role="status" sx={{ mb: 2 }}>
        선택한 앨범 {selectedAlbumIds.length}개
      </Typography>
      {/* 앨범 목록 테이블 */}
      <ProgressBar isLoading={loading}>
        {error ? (
          <Alert
            severity="error"
            action={
              <BasicButton variant="outlined" onClick={() => setRefetchTrigger((c) => c + 1)}>
                다시 시도
              </BasicButton>
            }
          >
            목록을 불러오지 못했습니다.
          </Alert>
        ) : !loading && albums.length === 0 ? (
          <NoData message="결과 없음" />
        ) : (
          <Stack spacing={7.5}>
            <TableContainer component={Paper} sx={{ boxShadow: 'none', maxWidth: '100%' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.grey.bg }}>
                    <TableCell padding="checkbox" sx={cellStyle}>
                      <Checkbox
                        inputProps={{ 'aria-label': '현재 페이지 전체 선택' }}
                        indeterminate={isIndeterminate}
                        checked={isAllOnPageSelected}
                        onChange={handleSelectAllClick}
                        size="small"
                        sx={{
                          color: theme.palette.grey.line,
                          '&.Mui-checked': { color: '#f16438' },
                          '&.MuiCheckbox-indeterminate': { color: '#f16438' },
                        }}
                      />
                    </TableCell>
                    {HEAD_CELLS.map((headCell, index) => (
                      <TableCell
                        key={headCell.id}
                        align="center"
                        sortDirection={sortField === headCell.id ? sortOrder : false}
                        sx={{
                          whiteSpace: 'nowrap',
                          borderRight:
                            index === HEAD_CELLS.length - 1
                              ? 'none'
                              : `1px solid ${theme.palette.grey.line}`,
                        }}
                      >
                        {headCell.sortable ? (
                          <TableSortLabel
                            active={true}
                            direction={sortField === headCell.id ? sortOrder : 'asc'}
                            onClick={() => handleSort(headCell.id)}
                            IconComponent={ArrowDropDownIcon}
                            sx={{
                              '&.Mui-active .MuiTableSortLabel-icon': {
                                color: theme.palette.black[3],
                              },
                            }}
                          >
                            <Typography fontSize={14} color={theme.palette.black[4]}>
                              {headCell.label}
                            </Typography>
                          </TableSortLabel>
                        ) : (
                          <Typography fontSize={14} color={theme.palette.black[4]}>
                            {headCell.label}
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {albums.map((album) => {
                    const isItemSelected = selectedAlbumIds.indexOf(album.albumId) !== -1;
                    return (
                      <TableRow key={album.albumId} hover selected={isItemSelected}>
                        <TableCell padding="checkbox" sx={cellStyle}>
                          <Checkbox
                            inputProps={{ 'aria-label': `${album.albumTitle} 선택` }}
                            checked={isItemSelected}
                            onClick={(event) => handleSelectClick(event, album.albumId)}
                            size="small"
                            sx={{
                              color: theme.palette.grey.line,
                              '&.Mui-checked': { color: '#f16438' },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ ...cellStyle, minWidth: 300 }}>
                          <Link
                            component={RouterLink}
                            to={
                              album.artistSlug
                                ? `/artists/${album.artistSlug}`
                                : `/artists/${album.artistSource?.toLowerCase()}/${album.artistSourceId}`
                            }
                            sx={{ textDecoration: 'none' }}
                          >
                            <Typography {...textStyle}>{album.artistName}</Typography>
                          </Link>
                        </TableCell>
                        <TableCell sx={{ ...cellStyle, minWidth: 392 }}>
                          <Link
                            component={RouterLink}
                            to={
                              album.albumSlug
                                ? `/reviews/${album.albumSlug}`
                                : `/reviews/local/${album.albumId}`
                            }
                            sx={{ textDecoration: 'none' }}
                          >
                            <Typography {...textStyle}>{album.albumTitle}</Typography>
                          </Link>
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          <Typography {...textStyle}>{album.releaseDate || '-'}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          <Typography {...textStyle}>{album.viewCount.toLocaleString()}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          <Typography {...textStyle}>
                            {album.reviewCount.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography {...textStyle}>
                            {album.bookmarkCount.toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              count={paginationInfo.totalPages}
              page={currentPage}
              onChange={handleChangePage}
            />
          </Stack>
        )}
      </ProgressBar>

      <Dialog
        open={Boolean(confirmation)}
        onClose={() => {
          if (!pending.current) setConfirmation(null);
        }}
        aria-labelledby="review-mark-confirm-title"
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { mx: 2, width: 'calc(100% - 32px)', minWidth: 0 } }}
      >
        <DialogTitle id="review-mark-confirm-title">P 마크 {action}</DialogTitle>
        <DialogContent>
          <Typography>
            선택한 앨범 {confirmation?.length || 0}개의 P 마크를 {action}하시겠습니까?
          </Typography>
          {mutationError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {mutationError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, p: 2 }}>
          <BasicButton disabled={busy} variant="outlined" onClick={() => setConfirmation(null)}>
            취소
          </BasicButton>
          <BasicButton disabled={busy} onClick={handleConfirm}>
            {busy ? '처리 중…' : '확인'}
          </BasicButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default function ReviewAlbumManagement(props) {
  const user = useUserStore((state) => state.user);
  const scope = JSON.stringify([
    user?.id ?? null,
    props.starred,
    props.searchParams.get('search') || '',
  ]);
  return <ReviewAlbumSession key={scope} {...props} />;
}
